// components/Tabs/web_parts/AgoraBase.tsx
// Agora会議システムの共通ロジックとユーティリティ関数

import { useEffect, useState, useRef } from 'react';
import AgoraRTC, { IAgoraRTCClient } from 'agora-rtc-sdk-ng';
import { message } from 'antd';
import { collection, query, where, getDocs, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from "../../../firebase/firebase";
import { 
  RemoteUser, 
  Participant, 
  AgoraClientState, 
  RemoteUserStates, 
  LayoutState, 
  ExitState,
  UidParseResult,
  AGORA_CONFIG,
  MeetingType,
  FirestoreCollection
} from './AgoraTypes';

/**
 * レスポンシブデザイン用のウィンドウサイズを監視するカスタムフック
 * @returns ウィンドウサイズと画面レイアウト情報
 */
export const useWindowSize = (): LayoutState => {
  const [size, setSize] = useState({ 
    width: window.innerWidth, 
    height: window.innerHeight 
  });
  
  useEffect(() => {
    const handleResize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { width, height } = size;
  const isMobilePortrait = width < 768 && height > width;
  const isWideLayout = !isMobilePortrait;
  
  return {
    width,
    height,
    isMobilePortrait,
    isWideLayout,
    mainHeight: '100vh' // デフォルト値、実際の使用時に計算される
  };
};

/**
 * 文字列形式のUIDを数値形式に変換する関数
 * Agoraは通常、数値型のUIDを使用するため、文字列の場合はハッシュ値を計算
 * @param uidString - 変換対象のUID文字列
 * @returns 数値形式のUID
 */
export const parseUid = (uidString: string | null): UidParseResult => {
  if (!uidString) {
    const randomUid = Math.floor(Math.random() * 1000000);
    return {
      numericUid: randomUid,
      originalUid: null
    };
  }
  
  // 数値の場合はそのまま返す
  if (!isNaN(Number(uidString))) {
    return {
      numericUid: Number(uidString),
      originalUid: uidString
    };
  }
  
  // 文字列の場合はハッシュ値を計算して数値に変換
  let hash = 0;
  for (let i = 0; i < uidString.length; i++) {
    const char = uidString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 32bit整数に変換
  }
  
  // 正の数に変換（Agoraは正の整数をUIDとして使用）
  const numericUid = Math.abs(hash);
  
  return {
    numericUid,
    originalUid: uidString
  };
};

/**
 * Firestoreから会議情報を取得する共通関数
 * @param channelId - チャンネルID
 * @param collectionName - Firestoreコレクション名
 * @param uid - 自分のUID（除外用）
 * @returns 会議名と参加者リスト
 */
export const fetchMeetingData = async (
  channelId: string, 
  collectionName: FirestoreCollection,
  uid: number | null
): Promise<{ meetingName: string; participants: Participant[] }> => {
  try {
    console.log(`📋 ${collectionName}から参加者情報を取得中... channelID=${channelId}`);

    const q = query(
      collection(db, collectionName),
      where("channelID", "==", channelId)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log(`${collectionName}: 参加者データなし`);
      return {
        meetingName: collectionName === 'agora_mimamori' ? '緊急みまもり会議' : '名称未設定',
        participants: []
      };
    }

    const docs = querySnapshot.docs;
    const meetingName = docs[0].data().meetingName || 
      (collectionName === 'agora_mimamori' ? '緊急みまもり会議' : '名称未設定');

    // 参加者データを数値UIDに統一
    const participantsData: Participant[] = docs
      .map((doc) => {
        const data = doc.data();
        // UIDを数値に確実に変換
        const parsedResult = parseUid(String(data.uid));
        return {
          uid: parsedResult.numericUid,
          name: data.name || data.regPerson || '名称不明',
          originalUid: data.uid,
          serialNumber: typeof data.serialNumber === "string" ? data.serialNumber : undefined,
          deviceUserName: undefined
        };
      })
      .filter((p) => p.uid !== uid); // 自分を除外

    // デバッグ情報の出力
    console.log(`📋 チャンネルID: ${channelId}`);
    console.log(`📋 取得したドキュメント数: ${docs.length}`);
    console.log(`📋 自分のUID: ${uid}`);
    console.log(`📋 フィルタ後参加者数: ${participantsData.length}`);
    
    docs.forEach((doc, index) => {
      const data = doc.data();
      console.log(`📋 Doc${index}: UID=${data.uid}(${typeof data.uid}), Name="${data.name}", RegPerson="${data.regPerson}", Serial="${data.serialNumber}"`);
    });

    return {
      meetingName,
      participants: participantsData
    };

  } catch (err) {
    console.error('会議データ取得エラー:', err);
    message.error('会議情報の取得に失敗しました。');
    return {
      meetingName: collectionName === 'agora_mimamori' ? '緊急みまもり会議' : '名称未設定',
      participants: []
    };
  }
};

/**
 * Firestoreから初期データ（名前とUID）を取得する共通関数
 * @param channelId - チャンネルID
 * @param collectionName - Firestoreコレクション名
 * @param userNameFromUrl - URLから取得した名前
 * @param uidFromUrl - URLから取得したUID
 * @returns 初期データ
 */
export const fetchInitialData = async (
  channelId: string,
  collectionName: FirestoreCollection,
  userNameFromUrl?: string | null,
  uidFromUrl?: string | null
): Promise<{ name: string; uid: number }> => {
  try {
    console.log(`📋 ${collectionName}コレクションからデータを取得中... channelID=${channelId}`);
    
    const q = query(
      collection(db, collectionName),
      where("channelID", "==", channelId),
      where("serialNumber", "==", "00000000")
    );
    const snapshot = await getDocs(q);
    
    let name = userNameFromUrl || 'ゲストユーザー';
    let uid: number;
    
    if (!snapshot.empty) {
      const docData = snapshot.docs[0].data();
      console.log('主催者データ:', docData);
      
      // 名前の設定
      if (docData.regPerson && !userNameFromUrl) {
        name = docData.regPerson;
        console.log("初期ユーザー名を設定:", docData.regPerson);
      }
      
      // UIDの設定ロジック
      if (uidFromUrl) {
        // URLからのUIDを数値に変換して使用
        const parsedResult = parseUid(uidFromUrl);
        uid = parsedResult.numericUid;
        console.log("URLからのUIDを設定:", uidFromUrl, "→ 変換後:", uid);
      } else if (docData.uid !== undefined) {
        // 通常会議の場合はFirestoreのUIDを使用
        const parsedResult = parseUid(String(docData.uid));
        uid = parsedResult.numericUid;
        console.log("初期UIDを設定:", docData.uid, "→ 変換後:", uid);
      } else {
        // UIDが見つからない場合はランダム生成
        uid = Math.floor(Math.random() * 1000000);
        console.log("UIDが見つからないためランダム生成:", uid);
      }
    } else {
      console.warn("該当するデータが見つかりません。");
      // データが見つからない場合の処理
      if (uidFromUrl) {
        const parsedResult = parseUid(uidFromUrl);
        uid = parsedResult.numericUid;
        console.log("URLからのフォールバックUIDを設定:", uid);
      } else {
        uid = Math.floor(Math.random() * 1000000);
        console.log("ランダムUIDを設定:", uid);
      }
    }
    
    return { name, uid };
    
  } catch (error) {
    console.error("初期データ取得エラー:", error);
    // エラーの場合でもUIDを設定
    const errorUid = uidFromUrl ? parseUid(uidFromUrl).numericUid : Math.floor(Math.random() * 1000000);
    console.log("エラー時UIDを設定:", errorUid);
    
    return {
      name: userNameFromUrl || 'ゲストユーザー',
      uid: errorUid
    };
  }
};

/**
 * アスペクト比を考慮した最適なビデオサイズを計算する関数
 * @param containerElement - ビデオコンテナ要素
 * @returns 最適なビデオサイズのスタイル
 */
export const calculateOptimalVideoSize = (containerElement: HTMLDivElement | null) => {
  if (!containerElement) return { width: '100%', height: '100%' };
    
  const containerWidth = containerElement.parentElement?.clientWidth || window.innerWidth;
  const containerHeight = containerElement.parentElement?.clientHeight || window.innerHeight;
    
  // 16:9のアスペクト比を維持
  const aspectRatio = 16 / 9;
    
  let videoWidth, videoHeight;
    
  // コンテナの高さに基づいてビデオの幅を計算
  const widthBasedOnHeight = containerHeight * aspectRatio;
    
  // コンテナの幅に基づいてビデオの高さを計算
  const heightBasedOnWidth = containerWidth / aspectRatio;
    
  // 幅または高さのどちらかがコンテナをはみ出す場合は、小さい方を使用
  if (widthBasedOnHeight <= containerWidth) {
    videoWidth = widthBasedOnHeight;
    videoHeight = containerHeight;
  } else {
    videoWidth = containerWidth;
    videoHeight = heightBasedOnWidth;
  }
    
  return {
    width: `${videoWidth}px`,
    height: `${videoHeight}px`,
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain' as const
  };
};

/**
 * 参加者名を取得するヘルパー関数
 * @param uid - 検索対象のUID
 * @param participants - 参加者リスト
 * @returns 参加者名
 */
export const getParticipantName = (uid: string, participants: Participant[]): string => {
  console.log('🔍 検索対象UID:', uid, 'Type:', typeof uid);
  console.log('🔍 利用可能な参加者リスト:', participants);
  const uidText = String(uid).trim();
  const normalizedUid = Number.parseInt(uidText, 10);
  const participant = participants.find((p) => {
    console.log('🔍 比較: Firestore UID:', p.uid, 'vs Agora UID:', uid);
    return p.uid === normalizedUid;
  });

  if (participant) return participant.name;

  const byOriginalUid = participants.find((p) => String(p.originalUid ?? "").trim() === uidText);
  if (byOriginalUid) return byOriginalUid.name;

  const byParsedOriginalUid = participants.find((p) => {
    const original = p.originalUid == null ? "" : String(p.originalUid);
    return parseUid(original).numericUid === normalizedUid;
  });
  if (byParsedOriginalUid) return byParsedOriginalUid.name;

  // UID 同期前でも、通常会議の Android 端末参加者が1名なら agoraitems.name で表示する。
  const androidDeviceCandidates = participants.filter(
    (p) =>
      p.uid === 0 &&
      p.serialNumber &&
      p.serialNumber !== "00000000" &&
      p.name &&
      p.name.trim().length > 0 &&
      p.name !== "名称不明"
  );
  if (androidDeviceCandidates.length === 1) {
    return androidDeviceCandidates[0].name;
  }

  if (normalizedUid === 99999999) return "利用者";
  return `参加者(${uid})`;
};

/**
 * 未参加者リストを取得する関数
 * @param participants - 全参加者リスト
 * @param remoteUsers - 現在接続中のリモートユーザー
 * @returns 未参加者リスト
 */
export const getNonParticipants = (participants: Participant[], remoteUsers: RemoteUser[]): Participant[] => {
  const activeUids = remoteUsers.map((user) => Number(user.uid));
  return participants.filter((p) => !activeUids.includes(p.uid));
};

/**
 * リモートビデオを再生する関数
 * @param user - リモートユーザー
 * @param container - ビデオコンテナ要素
 */
export const playRemoteVideo = (user: RemoteUser, container: HTMLDivElement | null) => {
  if (container && user.videoTrack) {
    try {
      user.videoTrack.play(container, { fit: "contain" });
    } catch (err) {
      console.error(`リモートビデオ再生エラー (UID: ${user.uid}):`, err);
    }
  }
};

/**
 * 会議タイプに応じたコレクション名を取得する関数
 * @param meetingType - 会議タイプ
 * @returns Firestoreコレクション名
 */
export const getCollectionName = (meetingType: MeetingType): FirestoreCollection => {
  return meetingType === MeetingType.MIMAMORI ? 'agora_mimamori' : 'agoraitems';
};

/**
 * Web 開設者が退室したとき、該当チャンネルの Firestore ドキュメントに closedAt を記録する。
 * Android の留守中 TV 会議検知（スナップショット）と整合させる。
 */
export async function markChannelClosedInFirestore(
  channelId: string | undefined,
  collectionName: FirestoreCollection
): Promise<void> {
  if (!channelId || channelId.trim().length === 0) {
    return;
  }
  try {
    const q = query(collection(db, collectionName), where('channelID', '==', channelId));
    const snap = await getDocs(q);
    if (snap.empty) {
      console.warn(`markChannelClosedInFirestore: no docs for channelID=${channelId} in ${collectionName}`);
      return;
    }
    const batch = writeBatch(db);
    const ts = serverTimestamp();
    snap.docs.forEach((d) => {
      batch.update(d.ref, { closedAt: ts });
    });
    await batch.commit();
    console.log(`markChannelClosedInFirestore: closedAt set for ${snap.docs.length} doc(s), ${collectionName}`);
  } catch (e) {
    console.error('markChannelClosedInFirestore failed', e);
  }
}

/**
 * デバッグログを出力する関数（開発環境でのみ有効）
 * @param message - ログメッセージ
 * @param data - ログデータ
 */
export const debugLog = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔧 [DEBUG] ${message}`, data || '');
  }
};